import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaChevronDown } from 'react-icons/fa';

const MultiselectDropdown = ({
  label,
  options = [],
  selectedValues = [],
  onChange,
  placeholder = 'Select options',
  disabled = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleOption = (value) => {
    const newSelected = [...(selectedValues || [])];
    const index = newSelected.indexOf(value);
    
    if (index === -1) {
      // Add if not selected
      newSelected.push(value);
    } else {
      // Remove if already selected
      newSelected.splice(index, 1);
    }
    
    onChange(newSelected);
  };

  const isSelected = (value) => {
    return selectedValues?.includes(value);
  };

  const getSelectedLabels = () => {
    return selectedValues.map(id => {
      const option = options.find(opt => opt.id === id);
      return option ? option.name : '';
    }).filter(Boolean).join(', ');
  };

  return (
    <div className={`mb-4 ${className}`} ref={dropdownRef}>
      {label && <label className="block text-gray-700 mb-2">{label}</label>}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-gray-400'
          }`}
        >
          {selectedValues?.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {getSelectedLabels().split(', ').map((label, index) => (
                <span 
                  key={index}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                >
                  {label}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <FaChevronDown className="h-4 w-4 text-gray-400" />
          </span>
        </button>
        
        {isOpen && !disabled && (
          <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
            {options.length === 0 ? (
              <div className="px-4 py-2 text-gray-500 text-sm">No options available</div>
            ) : (
              <ul className="max-h-60 overflow-auto">
                {options.map((option) => (
                  <li
                    key={option.id}
                    className={`px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center ${
                      isSelected(option.id) ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => toggleOption(option.id)}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected(option.id)}
                      onChange={() => {}}
                      className="mr-2"
                    />
                    <span>{option.name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

MultiselectDropdown.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  selectedValues: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ])),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

export default MultiselectDropdown;
